import React from 'react';
import { Link } from 'react-router-dom';
import { HeartHandshake, HandHeart, Sprout, Instagram, Youtube } from 'lucide-react';
import { XIcon } from '../components/ui/XIcon';
import { Button } from '../components/ui/button';
import PadaidLogo from '../components/ui/PadaidLogo';

// Nouveau design d'accueil inspiré du code fourni, adapté à notre thème (Navbar/Footer conservés)
const HomeAlt = () => {
  const heroBg = {
    backgroundImage:
      "url('data:image/svg+xml;utf8,&lt;svg xmlns=\"http://www.w3.org/2000/svg\" width=\"100\" height=\"100\" viewBox=\"0 0 100 100\"&gt;&lt;rect width=\"100\" height=\"100\" fill=\"%23ffffff\"/&gt;&lt;path d=\"M0 0 L100 100\" stroke=\"%23e5e7eb\" stroke-width=\"2\"/&gt;&lt;path d=\"M100 0 L0 100\" stroke=\"%23e5e7eb\" stroke-width=\"2\"/&gt;&lt;/svg&gt;')",
    backgroundSize: '30px',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/40 text-foreground">
      {/* Styles dédiés (animations) */}
      <style>{`
        @keyframes heartbeat { 0%{transform:rotate(45deg) scale(1)} 5%{transform:rotate(45deg) scale(1.08)} 10%{transform:rotate(45deg) scale(1)} 15%{transform:rotate(45deg) scale(1.08)} 20%{transform:rotate(45deg) scale(1)} 100%{transform:rotate(45deg) scale(1)} }
      `}</style>

      {/* Hero */}
      <section className="relative flex flex-col items-center text-center px-4 py-16 sm:py-20" style={heroBg}>
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-foreground">
            Agissons ensemble pour un monde meilleur
          </h1>
          <p className="tagline text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto">
            Notre association œuvre quotidiennement pour soutenir les plus démunis et construire une société plus solidaire
          </p>

          {/* Logo PADAID au centre */}
          <div className="flex items-center justify-center my-10" data-testid="homealt-logo">
            <PadaidLogo size="xxl" showText={false} interactive={false} />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/donate">
              <Button size="lg" className="rounded-full px-6 bg-destructive hover:bg-destructive/90">Faire un don</Button>
            </Link>
            <Link to="/membership">
              <Button size="lg" variant="secondary" className="rounded-full px-6">Devenir bénévole</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="py-16 bg-card">
        <h2 className="section-title text-center text-3xl font-bold mb-10 text-foreground">Nos valeurs</h2>
        <div className="values-grid max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-4">
          <div className="value-card bg-muted/40 rounded-xl p-6 text-center border-t-4 border-primary shadow-sm hover:-translate-y-1 transition">
            <div className="flex items-center justify-center mb-4 text-primary"><HeartHandshake size={48} /></div>
            <h3 className="text-xl font-semibold mb-2">Compassion</h3>
            <p className="text-muted-foreground">La compassion est au cœur de toutes nos actions. Nous œuvrons avec empathie et bienveillance envers tous.</p>
          </div>
          <div className="value-card bg-muted/40 rounded-xl p-6 text-center border-t-4 border-primary shadow-sm hover:-translate-y-1 transition">
            <div className="flex items-center justify-center mb-4 text-primary"><HandHeart size={48} /></div>
            <h3 className="text-xl font-semibold mb-2">Solidarité</h3>
            <p className="text-muted-foreground">Nous croyons en la force de la solidarité pour construire une société plus juste et équitable pour tous.</p>
          </div>
          <div className="value-card bg-muted/40 rounded-xl p-6 text-center border-t-4 border-primary shadow-sm hover:-translate-y-1 transition">
            <div className="flex items-center justify-center mb-4 text-primary"><Sprout size={48} /></div>
            <h3 className="text-xl font-semibold mb-2">Espoir</h3>
            <p className="text-muted-foreground">Nous travaillons à créer un avenir meilleur, porteurs d'espoir pour les générations futures.</p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 bg-background">
        <h2 className="section-title text-center text-3xl font-bold mb-6 text-foreground">Notre mission</h2>
        <div className="mission-content max-w-4xl mx-auto text-center px-4 text-[17px] leading-relaxed text-muted-foreground">
          <p className="mb-4">Notre association a pour mission de venir en aide aux personnes les plus vulnérables, en luttant contre l'exclusion et la pauvreté. Nous menons des actions concrètes sur le terrain pour apporter un soutien matériel, moral et social à ceux qui en ont besoin.</p>
          <p>Chaque jour, grâce à nos donateurs et bénévoles, nous pouvons changer des vies et redonner espoir à des familles entières. Rejoignez-nous dans ce combat essentiel pour plus de solidarité.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-primary to-secondary text-primary-foreground text-center">
        <h2 className="text-3xl font-bold mb-6">Vous aussi, participez à notre action</h2>
        <div className="flex flex-wrap items-center justify-center gap-3 px-4 mb-6">
          <Link to="/donate"><Button size="lg" className="rounded-full px-6 bg-background text-foreground hover:bg-white/80">Faire un don</Button></Link>
          <Link to="/membership"><Button size="lg" variant="secondary" className="rounded-full px-6">Devenir bénévole</Button></Link>
          <Link to="/contact"><Button size="lg" className="rounded-full px-6 bg-background text-foreground hover:bg-white/80">Parrainer un projet</Button></Link>
        </div>
        <div className="flex items-center justify-center gap-4">
          <a href="https://x.com/Padaid_Asso" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:opacity-90">
            <span className="sr-only">X (Twitter)</span>
            <XIcon className="w-6 h-6" />
          </a>
          <a href="https://www.instagram.com/padaid_asso" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:opacity-90">
            <span className="sr-only">Instagram</span>
            <Instagram className="w-6 h-6" />
          </a>
          <a href="https://www.youtube.com/@PADAID" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:opacity-90">
            <span className="sr-only">YouTube</span>
            <Youtube className="w-7 h-7" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default HomeAlt;